import React, {Dispatch, FC, SetStateAction, useEffect, useState} from 'react';
import {Checkbox, Col, Form, Input, message, Radio, Row} from "antd";
import {ScheduleType} from "../../../../../proto/generated/api_entities_pb";
import dayjs, {Dayjs} from "dayjs";
import utc from 'dayjs/plugin/utc';
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {useNavigate} from "react-router";
import {Route} from "../../../../../config/acl/routes/Route";
import {routes} from "../../../../../config/acl/routes";
import {clearEditEvent, editEvent} from "../../../../../store/reducers/scheduleReducer/scheduleReducer";
import {CustomDatePicker} from "../../../../../components/UI/AntCustom/CustomDatePicker";
import {DateAndTimeRangePicker} from "../../../../../components/UI/AntCustom/DateAndTimeRangePicker";
import {
  fetchAirports,
  fetchInitialAirportForSelect,
  getInitialAirportForSelect
} from "../../../../../utils/helpers/selectDataFetchers/fetchAirports";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import WarningIcon from "../../../../../media/icons/warning.svg?react";
import PlaneGreyIcon from "../../../../../media/icons/plane-grey.svg?react";
import {useAirportFilter} from "../../../../../utils/hooks/useAirportFilter";
import {ScheduleDeleteModal} from "./ScheduleDeleteModal";
import {DeleteOutlined} from "@ant-design/icons";
import {dayjsWithOffset, dayjsToUTCDate} from "../../../../../utils/helpers/dateHelpers/dateHelpers";
import {
  fetchOwnPlanes,
  getInitialOwnPlaneForSelect
} from "../../../../../utils/helpers/selectDataFetchers/fetchOwnPlanes";
import {IPlaneEvent} from "../../../../../store/reducers/scheduleReducer/scheduleReducerTypes";
import {renderSuccessModal} from "../../../../../components/UI/StatusModals/renderSuccessModal";
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CommonUnitMessages} from "../../../../../intl/commonMessages/CommonUnitMessages";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {onFinishTrimmed} from "../../../../../utils/helpers/formHelpers/onFinishTrimmed";
import {API} from "../../../../../API/API";
import {AirportApiGetAirportRequest} from "../../../../../proto/generated/api_entities_pb";
import {mapAirportEntityToIAirport} from "../../../../../store/reducers/fleetReducer/fleetMappers";

dayjs.extend(utc);

const ScheduleEditModalMessages = defineMessages({
  title: {
    id: "scheduleEditModal.title",
    defaultMessage: "Edit event",
  },
  success: {
    id: "scheduleEditModal.success",
    defaultMessage: "The event has been successfully updated",
  },
  emptyLegWarning: {
    id: "scheduleEditModal.emptyLegWarning",
    defaultMessage: "This empty leg event should be edited in the Empty Legs list. Save and delete actions are disabled.",
  }
})

interface IScheduleCreateFormData {
  planeId: number;
  eventType: ScheduleType;
  dateRange: [Dayjs, Dayjs];
  airportFromId: number;
  airportToId?: number;
  eventName?: string;
  refuelingCount?: number; // Количество дозаправок (для ORDER_REQUEST и EMPTY_LEG)
  pax?: number; // Количество пассажиров (для ORDER_REQUEST)
  isPublic?: boolean; // Публичный доступ
}

interface IScheduleCreateModal {
  planeEvent: IPlaneEvent;
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
}

export const ScheduleEditModal: FC<IScheduleCreateModal> = ({planeEvent, isModalVisible, setIsModalVisible}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    isLoading,
    isSuccess,
    errorMessage,
  } = useAppSelector(state => state.schedule.edit);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [eventTypeRadioValue, setEventTypeRadioValue] = useState<ScheduleType>(planeEvent.eventType);
  const [airportFromOffset, setAirportFromOffset] = useState<number | null>(null);
  const [airportToOffset, setAirportToOffset] = useState<number | null>(null);
  
  // Check if this is an empty leg event that should be edited in the empty legs list
  // Only make it read-only if hasEmptyLeg is explicitly true AND the empty leg is not archived
  // If hasEmptyLeg is false/undefined, the empty leg has been removed, so allow normal editing
  // If isEmptyLegArchived is true, the empty leg is archived, so allow normal editing
  const isReadOnlyEmptyLeg = planeEvent.eventType === ScheduleType.EMPTY_LEG 
    && planeEvent.hasEmptyLeg === true 
    && planeEvent.isEmptyLegArchived !== true;

  useEffect(() => {
    return () => {
      dispatch(clearEditEvent());
    }
  }, [])

  useEffect(() => {
    setEventTypeRadioValue(planeEvent.eventType);
  }, [planeEvent.eventType])

  // Загрузка информации об аэропортах при инициализации
  useEffect(() => {
    const loadAirportInfo = async () => {
      if (planeEvent.airportFrom) {
        setAirportFromOffset(planeEvent.airportFrom.timezone?.offset ?? null);
      } else {
        setAirportFromOffset(null);
      }
      if (planeEvent.airportTo) {
        setAirportToOffset(planeEvent.airportTo.timezone?.offset ?? null);
      } else {
        setAirportToOffset(null);
      }
    };

    if (isModalVisible) {
      loadAirportInfo();
    }
  }, [planeEvent, isModalVisible])

  const handleFormValuesChange = async (changedValues: any) => {
    const fieldName = Object.keys(changedValues)[0];

    if (fieldName === "eventType") {
      const value = changedValues[fieldName];
      setEventTypeRadioValue(value)
    } else if (["airportFromId", "airportToId"].includes(fieldName)) {
      // Загружаем информацию об аэропортах при изменении
      const airportFromId = fieldName === 'airportFromId' ? changedValues[fieldName] : form.getFieldValue('airportFromId');
      const airportToId = fieldName === 'airportToId' ? changedValues[fieldName] : form.getFieldValue('airportToId');

      if (airportFromId) {
        try {
          const req = new AirportApiGetAirportRequest().setAirportid(airportFromId);
          const airportEntity = (await API.getAirportById(req)).getAirport();
          if (airportEntity) {
            const airport = mapAirportEntityToIAirport(airportEntity);
            setAirportFromOffset(airport.timezone?.offset ?? null);
          } else {
            setAirportFromOffset(null);
          }
        } catch (error) {
          console.error('Failed to load airport from:', error);
          setAirportFromOffset(null);
        }
      } else {
        setAirportFromOffset(null);
      }

      if (airportToId) {
        try {
          const req = new AirportApiGetAirportRequest().setAirportid(airportToId);
          const airportEntity = (await API.getAirportById(req)).getAirport();
          if (airportEntity) {
            const airport = mapAirportEntityToIAirport(airportEntity);
            setAirportToOffset(airport.timezone?.offset ?? null);
          } else {
            setAirportToOffset(null);
          }
        } catch (error) {
          console.error('Failed to load airport to:', error);
          setAirportToOffset(null);
        }
      } else {
        setAirportToOffset(null);
      }
    }
  }

  useEffect(() => {
    if (!isLoading) {
      if (isSuccess)
        renderSuccessModal(intl.formatMessage(ScheduleEditModalMessages.success), () => setIsModalVisible(false));
      if (errorMessage)
        message.error(errorMessage);
    }
  }, [isSuccess, errorMessage, isLoading])

  const onFinish = (values: IScheduleCreateFormData) => {
    // Prevent submission for read-only empty leg events
    if (isReadOnlyEmptyLeg) {
      return;
    }
    
    dispatch(editEvent({
      eventId: planeEvent.id,
      eventType: values.eventType,
      eventName: values.eventName,
      planeId: values.planeId,
      dateFrom: dayjsToUTCDate(values.dateRange[0]),
      dateTo: dayjsToUTCDate(values.dateRange[1]),
      airportFromId: values.airportFromId,
      airportToId: values.airportToId,
      refuelingCount: values.refuelingCount,
      pax: values.pax,
      isPublic: values.isPublic,
    }));
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const fromAirportFilter = useAirportFilter(form, 'airportToId');
  const toAirportFilter = useAirportFilter(form, 'airportFromId');

  const getInitialAirportFromCallback = () => {
    const currentAirportFromId = form.getFieldValue('airportFromId');
    if (!currentAirportFromId) {
      return Promise.resolve(undefined);
    }
    return fetchInitialAirportForSelect(currentAirportFromId);
  }

  // Функция для конвертации UTC Date в Dayjs для отображения в форме с пометкой UTC
  // Проблема: DateAndTimePicker работает с локальным временем, но нам нужно показать UTC время
  // Решение: создаем Date объект через Date.UTC(), который создаст UTC timestamp,
  // но затем создаем новый Date с этим timestamp, который будет показывать UTC компоненты как локальное время
  const convertUTCDateToDayjs = (date: Date): Dayjs => {
    // ВАЖНО: форма должна показывать ЛОКАЛЬНОЕ время, как в календаре и во всплывающем окне
    // Например: UTC 2025-12-09 20:00 = локальное 2025-12-10 00:00 (GMT+4)
    // Просто создаем Dayjs объект из Date - он автоматически конвертирует UTC в локальное время
    return dayjs(date);
  };

  const initialValues: IScheduleCreateFormData = {
    planeId: planeEvent.plane.id,
    eventType: planeEvent.eventType,
    dateRange: [convertUTCDateToDayjs(planeEvent.dateFrom), convertUTCDateToDayjs(planeEvent.dateTo)],
    airportFromId: planeEvent.airportFrom.id,
    airportToId: planeEvent.airportTo?.id,
    eventName: planeEvent.eventName,
    refuelingCount: planeEvent.refuelingCount,
    pax: planeEvent.pax,
    isPublic: planeEvent.isPublic,
  }

  return (
    <CustomModal
      title={intl.formatMessage(ScheduleEditModalMessages.title)}
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      centered
      footer={null}
      width={700}
    >
      {isReadOnlyEmptyLeg && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          backgroundColor: '#fff7e6', 
          border: '1px solid #ffd591',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <WarningIcon style={{ width: 20, height: 20, color: '#fa8c16', flexShrink: 0 }} />
          <span style={{ color: '#ad6800' }}>
            This empty leg event should be edited in the{' '}
            <span
              onClick={() => {
                setIsModalVisible(false);
                navigate(routes[Route.emptyLegs].path);
              }}
              style={{
                color: '#1890ff',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 500
              }}
            >
              Empty Legs
            </span>
            {' '}list. Save and delete actions are disabled.
          </span>
        </div>
      )}
      
      <Form
        form={form}
        onFinish={(values) => {
          if (isReadOnlyEmptyLeg) {
            return;
          }
          onFinishTrimmed(values, onFinish);
        }}
        onFinishFailed={onFinishFailed}
        layout={'vertical'}
        initialValues={initialValues}
        onValuesChange={handleFormValuesChange}
      >
        {(eventTypeRadioValue !== ScheduleType.ORDER_REQUEST && 
          eventTypeRadioValue !== ScheduleType.EMPTY_LEG) &&
          <Col flex={1}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.eventNameLabel)}
              name={'eventName'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <Input 
                placeholder={intl.formatMessage(CommonFormMessages.eventNamePlaceholder)}
                disabled={isReadOnlyEmptyLeg}
              />
            </CustomFieldInput>
          </Col>
        }

        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.planeLabel)}
            name={'planeId'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <DebounceSelect
              fetchOptions={fetchOwnPlanes}
              placeholder={intl.formatMessage(CommonFormMessages.planePlaceholder)}
              defaultOptions={[getInitialOwnPlaneForSelect(planeEvent.plane)]}
              disabled={isReadOnlyEmptyLeg}
            />
          </CustomFieldInput>
        </Col>

        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.eventTypeLabel)}
            name={'eventType'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <Radio.Group disabled={isReadOnlyEmptyLeg}>
              <Radio
                value={ScheduleType.ORDER_REQUEST}>{intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio)}</Radio>;
              <Radio
                value={ScheduleType.EMPTY_LEG}>{intl.formatMessage(CommonFormMessages.eventTypeEmptyLegRadio)}</Radio>;
              <Radio value={ScheduleType.MAINTENANCE}>{intl.formatMessage(CommonFormMessages.eventTypeMaintenanceRadio)}</Radio>;
              <Radio value={ScheduleType.OTHER}>{intl.formatMessage(CommonFormMessages.eventTypeParkingRadio)}</Radio>;
              <Radio value={ScheduleType.CUSTOM}>{intl.formatMessage(CommonFormMessages.eventTypeCustomRadio)}</Radio>;
            </Radio.Group>
          </CustomFieldInput>
        </Col>

        {(eventTypeRadioValue === ScheduleType.ORDER_REQUEST || eventTypeRadioValue === ScheduleType.EMPTY_LEG) &&
          <>
            <Col flex={1}>
              <Row gutter={16}>
                <Col flex="0 1 280px">
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.fromAirportLabel)}
                    name={'airportFromId'}
                    rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
                  >
                    <DebounceSelect
                      key={fromAirportFilter.key}
                      fetchOptions={fromAirportFilter.fetchOptions}
                      placeholder={intl.formatMessage(CommonFormMessages.fromAirportPlaceholder)}
                      showSearch={true}
                      defaultOptions={[getInitialAirportForSelect(planeEvent.airportFrom)]}
                      disabled={isReadOnlyEmptyLeg}
                    />
                  </CustomFieldInput>
                </Col>

                <Col flex={1} style={{display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px', paddingLeft: '8px', paddingRight: '8px'}}>
                  <PlaneGreyIcon />
                  <div style={{display: 'flex', alignItems: 'center', position: 'relative', width: '100%'}}>
                    <div style={{width: '100%', height: '2px', backgroundColor: '#808080', position: 'absolute'}}></div>
                    <div style={{width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#808080', position: 'absolute', right: 0}}></div>
                  </div>
                </Col>

                <Col flex="0 1 280px">
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.toAirportLabel)}
                    name={'airportToId'}
                    rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
                  >
                    <DebounceSelect
                      key={toAirportFilter.key}
                      fetchOptions={toAirportFilter.fetchOptions}
                      placeholder={intl.formatMessage(CommonFormMessages.toAirportPlaceholder)}
                      showSearch={true}
                      defaultOptions={planeEvent.airportTo && [getInitialAirportForSelect(planeEvent.airportTo)]}
                      disabled={isReadOnlyEmptyLeg}
                    />
                  </CustomFieldInput>
                </Col>
              </Row>
            </Col>
          </>
        }

        {(eventTypeRadioValue === ScheduleType.CUSTOM ||
          eventTypeRadioValue === ScheduleType.MAINTENANCE ||
          eventTypeRadioValue === ScheduleType.OTHER) &&
          <Col flex={1}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.stayAirportLabel)}
              name={'airportFromId'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DebounceSelect
                fetchOptions={fetchAirports}
                placeholder={intl.formatMessage(CommonFormMessages.stayAirportPlaceholder)}
                showSearch={true}
                defaultOptionsFetchCallback={getInitialAirportFromCallback}
                disabled={isReadOnlyEmptyLeg}
              />
            </CustomFieldInput>
          </Col>
        }

        <Col flex={1}>
          <div style={{marginBottom: '16px'}}>
            <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.availableDatesLabel)} (UTC)`}
              name={'dateRange'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DateAndTimeRangePicker
                format="DD.MM.YYYY"
                placeholder={[intl.formatMessage(CommonUnitMessages.from), intl.formatMessage(CommonUnitMessages.to)]}
                inputReadOnly={true} 
                placement={"bottomLeft"}
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                airportFromOffset={airportFromOffset}
                airportToOffset={airportToOffset}
                containerHeight="100%"
                disabled={isReadOnlyEmptyLeg}
              />
            </CustomFieldInput>
            <div style={{color: '#808080', fontSize: '9px', display: 'flex', alignItems: 'center', gap: '4px'}}>
              <WarningIcon style={{width: '12px', height: '12px', fill: '#808080'}} />
              {intl.formatMessage(CommonFormMessages.airportHoursWarning)}
            </div>
          </div>
        </Col>

        {eventTypeRadioValue === ScheduleType.ORDER_REQUEST &&
          <>
            <Col flex={1}>
              <Row gutter={16}>
                <Col flex={1}>
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.refuelingCountLabel)}
                    name={'refuelingCount'}
                  >
                    <CustomInputNumber 
                      controls={true} 
                      placeholder={intl.formatMessage(CommonFormMessages.commonPlaceholder)}
                      style={{width: "100%"}}
                      min={0}
                      disabled={isReadOnlyEmptyLeg}
                    />
                  </CustomFieldInput>
                </Col>
                <Col flex="0 1 120px">
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.paxLabel)}
                    name={'pax'}
                    rules={[
                      {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                      {min: 1, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinPax)},
                      {max: 100, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMaxPax)}
                    ]}
                  >
                    <CustomInputNumber 
                      controls={true} 
                      placeholder={intl.formatMessage(CommonFormMessages.paxPlaceholder)}
                      style={{width: "100%"}}
                      disabled={isReadOnlyEmptyLeg}
                    />
                  </CustomFieldInput>
                </Col>
              </Row>
            </Col>
          </>
        }

        {eventTypeRadioValue === ScheduleType.EMPTY_LEG &&
          <>
            <Col flex={1}>
              <CustomFieldInput
                label={intl.formatMessage(CommonFormMessages.refuelingCountLabel)}
                name={'refuelingCount'}
              >
                <CustomInputNumber 
                  controls={true} 
                  placeholder={intl.formatMessage(CommonFormMessages.commonPlaceholder)}
                  style={{width: "100%"}}
                  min={0}
                  disabled={isReadOnlyEmptyLeg}
                />
              </CustomFieldInput>
            </Col>
          </>
        }

        <Col flex={1}>
          <CustomFieldInput
            name={'isPublic'}
            valuePropName="checked"
          >
            <Checkbox disabled={isReadOnlyEmptyLeg}>
              {intl.formatMessage(CommonFormMessages.openForPublicSchedule)}
            </Checkbox>
          </CustomFieldInput>
        </Col>

        <CustomFieldInput>
          <CustomButton
            block
            type={'primary'}
            htmlType={'submit'}
            loading={isLoading}
            disabled={isReadOnlyEmptyLeg}
          >
            {intl.formatMessage(CommonButtonMessages.save)}
          </CustomButton>
        </CustomFieldInput>

        <CustomButton 
          type={"link"} 
          block 
          danger 
          onClick={() => {
            if (!isReadOnlyEmptyLeg) {
              setIsDeleteModalVisible(true);
            }
          }}
          disabled={isReadOnlyEmptyLeg}
        >
          <DeleteOutlined/> {intl.formatMessage(CommonButtonMessages.delete)}
        </CustomButton>
      </Form>

      {isDeleteModalVisible &&
        <ScheduleDeleteModal planeEvent={planeEvent} isModalVisible={isDeleteModalVisible}
                             setIsModalVisible={setIsDeleteModalVisible}
                             closeOuterEditModal={() => setIsModalVisible(false)}/>}
    </CustomModal>
  );
};
